import React, { ReactElement, useEffect, useMemo, useRef, useState } from "react";
import { CustomModal } from "../CustomModal";
import { Input } from "antd";
import { useTranslate } from "@obsbot/remo-i18n";
import { filterInputValue } from "@obsbot/remo-utils";
import { withMessage } from "../../HOC/withMessage";
import "./index.less";
import { validatePassword } from "../LoginPage/LoginWithCode/validators";
import { checkPassSame } from "@obsbot/remo-server-api";

enum PassSameType {
    new = 0,
    confirm = 1,
}

export interface ChangePasswordProps {
    visible: boolean;
    validCode: string;
    isPassSameError: boolean;
    noSameVerify?: boolean;
    setIsPassSameError: (isPassSameError: boolean) => void;
    setVisible: (visible: boolean) => void;
    onConfirm: (code: string, newPass: string) => void;
    hideDesc?: boolean;
}

export const ChangePasswordModal: React.FC<ChangePasswordProps> = withMessage(
    ({
        visible,
        validCode,
        message,
        setVisible,
        onConfirm,
        hideDesc = false,
        noSameVerify = false,
    }): ReactElement => {
        const t = useTranslate();
        // 记录账号验证码input
        const [userPassNew, setUserPassNew] = useState("");
        // 记录新密码input
        const [userPassConfirm, setUserPassConfirm] = useState("");
        // 是否校验不通过
        const [isPassNotValid, setIsPassNotValid] = useState<{
            new: boolean;
            confirm: boolean;
            msg: string;
        }>({
            new: false,
            confirm: false,
            msg: "",
        });
        // 是否是一致的密码
        const isPassSameObj = useRef<Record<string, boolean>>({});
        // 密码是否一致
        const [isPassSameType, setIsPassSameType] = useState<Record<PassSameType, boolean>>({
            [PassSameType.new]: false,
            [PassSameType.confirm]: false,
        });
        const isBothSame = useMemo(() => {
            return isPassSameType[PassSameType.new] && isPassSameType[PassSameType.confirm];
        }, [isPassSameType]);
        // 重置
        useEffect(() => {
            setUserPassNew("");
            setUserPassConfirm("");
            isPassSameObj.current = {};
            if (!visible) {
                setIsPassNotValid({
                    new: false,
                    confirm: false,
                    msg: "",
                });
            }
        }, [visible]);
        const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
            if (e.key === "Enter") {
                handleConfirm();
            }
        };
        const handleConfirm = async (): Promise<void> => {
            if (!userPassNew && !userPassConfirm) {
                message.warning(
                    t("noInputDouble", {
                        text1: t("changePass.newPass"),
                        text2: t("changePass.confirmPass"),
                    }),
                );
                return;
            }
            if (!userPassNew) {
                message.warning(t("noInputSingle", { text1: t("changePass.newPass") }));
                return;
            }
            if (!userPassConfirm) {
                message.warning(t("noInputSingle", { text1: t("changePass.confirmPass") }));
                return;
            }
            if (isPassSameType[PassSameType.new] || isPassSameType[PassSameType.confirm]) {
                return;
            }
            const newPassValidate = validatePassword(userPassNew);
            const confirmPassValidate = validatePassword(userPassConfirm);
            if (!newPassValidate || !confirmPassValidate) {
                setIsPassNotValid({
                    new: !newPassValidate,
                    confirm: !confirmPassValidate,
                    msg: "formatError",
                });
                return;
            }
            if (userPassNew !== userPassConfirm) {
                setIsPassNotValid({
                    new: false,
                    confirm: true,
                    msg: "confirmNotSame",
                });
                return;
            }
            onConfirm && onConfirm(validCode, userPassNew);
        };
        // 处理失焦
        const handleBlur = async (input: string, updateType: PassSameType): Promise<void> => {
            if (!input.length) {
                return;
            }
            const newPassValidate = validatePassword(input);
            if (!newPassValidate) {
                setIsPassNotValid(prev =>
                    updateType === PassSameType.new
                        ? {
                              ...prev,
                              new: !newPassValidate,
                              msg: "formatError",
                          }
                        : {
                              ...prev,
                              confirm: !newPassValidate,
                              msg: "formatError",
                          },
                );
                return;
            }
            if (isPassSameObj.current[input]) {
                setIsPassSameType(prev => ({
                    ...prev,
                    [updateType]: true,
                }));
                return;
            } else if (isPassSameObj.current[input] === false) {
                setIsPassSameType(prev => ({
                    ...prev,
                    [updateType]: false,
                }));
                return;
            }
            if (!noSameVerify) {
                const { data } = await checkPassSame(input);
                isPassSameObj.current[input] = data === "true" ? true : false;
            }
            setIsPassSameType(prev => ({
                ...prev,
                [updateType]: isPassSameObj.current[input],
            }));
        };
        return (
            <CustomModal
                className="ChangePasswordModal"
                destroyOnHidden={true}
                footer={
                    <div className="password-modal-button-group modal-button-group-center">
                        <div
                            className="normal-button"
                            onClick={() => {
                                setUserPassNew("");
                                setUserPassConfirm("");
                                setVisible && setVisible(false);
                            }}
                        >
                            {t("cancel")}
                        </div>
                        <div className="group-modal-button-item" onClick={handleConfirm}>
                            {t("done")}
                        </div>
                    </div>
                }
                open={visible}
                renderHeader={t("changePass.title")}
                setVisible={setVisible}
                onClickClose={() => {
                    setUserPassNew("");
                    setUserPassConfirm("");
                }}
            >
                {hideDesc ? null : (
                    <div className="bind-modal-desc">
                        <span>{t("changePass.description")}</span>
                    </div>
                )}
                <div className="bind-modal-form">
                    <div className="bind-modal-account-verify">
                        <div className="bind-modal-title bind-modal-account-title">
                            <span>{t("changePass.newPass")}</span>
                        </div>

                        <Input.Password
                            className={`custom-modal-form-input ${
                                isPassSameType[PassSameType.new] || isBothSame || isPassNotValid.new
                                    ? "custom-modal-form-input-error"
                                    : ""
                            }`}
                            placeholder={t("changePass.newPassPlaceholder")}
                            value={userPassNew}
                            onBlur={() => handleBlur(userPassNew, PassSameType.new)}
                            onChange={e => {
                                setUserPassNew(filterInputValue(e.target.value));
                                const newObj = { ...isPassNotValid, new: false };
                                if (isPassNotValid && isPassNotValid.msg === "confirmNotSame") {
                                    newObj.msg = "";
                                    newObj.confirm = false;
                                }
                                setIsPassNotValid(newObj);
                            }}
                            onKeyDown={handleKeyDown}
                        />
                        {(isPassNotValid.new || isBothSame || isPassSameType[PassSameType.new]) && (
                            <div className="modal-form-error">
                                {isPassNotValid.new
                                    ? t(`changePass.${isPassNotValid.msg}`)
                                    : t("changePass.samePass")}
                            </div>
                        )}
                    </div>
                    <div className="bind-modal-newpass">
                        <div className="bind-modal-title bind-modal-account-title">
                            <span>{t("changePass.confirmPass")}</span>
                        </div>
                        <div className="bind-modal-input-with-error">
                            <Input.Password
                                className={`custom-modal-form-input ${
                                    isBothSame ||
                                    isPassSameType[PassSameType.confirm] ||
                                    isPassNotValid.confirm
                                        ? "custom-modal-form-input-error"
                                        : ""
                                }`}
                                placeholder={t("changePass.confirmPassPlaceholder")}
                                value={userPassConfirm}
                                onBlur={() => handleBlur(userPassConfirm, PassSameType.confirm)}
                                onChange={e => {
                                    setUserPassConfirm(filterInputValue(e.target.value));
                                    const newObj = { ...isPassNotValid, confirm: false };
                                    if (isPassNotValid && isPassNotValid.msg === "confirmNotSame") {
                                        newObj.msg = "";
                                        newObj.new = false;
                                    }
                                    setIsPassNotValid(newObj);
                                }}
                                onKeyDown={handleKeyDown}
                            />
                            {(isPassNotValid.confirm ||
                                isBothSame ||
                                isPassSameType[PassSameType.confirm]) && (
                                <div className="modal-form-error">
                                    {isPassNotValid.confirm
                                        ? t(`changePass.${isPassNotValid.msg}`)
                                        : t("changePass.samePass")}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </CustomModal>
        );
    },
);
